import { Label } from "@/components/ui/label";
import { Controller } from "react-hook-form";
import { useState } from "react";

import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/library/dropdown-menu";
import { ChevronDown, Search } from "lucide-react";

import { Badge } from "@/components/ui/others/Badges";
import {
  getCustomInputScopeErrorMessage,
  hasCustomInputScopeError,
} from "@/utils/formErrors";
import { getCfDisplayName } from "@/features/customField/utils/cfDisplayName";
import { useRequiredFieldMark } from "@/features/leads/requirements/requiredFieldMarks";
import { RequiredStar } from "@/features/leads/requirements/requiredFieldMarks.ui";

type Props = {
  val: any;
  index: number;
  errors: any;
  control: any;
};

export default function TagsMultySelect({
  val,
  index,
  errors,
  control,
}: Props) {
  // Local to the field: cleared whenever the menu closes so the next open
  // starts on the full option list.
  const [search, setSearch] = useState("");
  const scopeErr = errors?.customInputScopes?.[index];
  const scopeMsg = getCustomInputScopeErrorMessage(scopeErr);
  const scopeInvalid = hasCustomInputScopeError(scopeErr);
  // Stage-requirement star (null outside a marks provider → no change).
  const required = useRequiredFieldMark("custom", val?.id);

  return (
    <div className="space-y-2 w-full col-span-2 mb-4">
      <Label className={scopeInvalid ? "text-red-500" : ""}>
        {getCfDisplayName(val)}
        <RequiredStar mark={required} className="-ml-1.5" />
      </Label>

      <Controller
        name={`customInputScopes.${index}`}
        control={control}
        defaultValue={{
          customInputId: val.id,
          optionIds: [],
        }}
        render={({ field }) => {
          const selectedValues: string[] = Array.isArray(field.value?.optionIds)
            ? field.value.optionIds
            : [];

          const toggleValue = (value: string) => {
            const updated = selectedValues.includes(value)
              ? selectedValues.filter((v) => v !== value)
              : [...selectedValues, value];

            field.onChange({
              customInputId: val.id,
              optionIds: updated,
            });
          };

          const selectedOptions = val.options.filter((op: any) =>
            selectedValues.includes(String(op.id))
          );

          // Filter only — the tenant's own option order is preserved.
          const query = search.trim().toLowerCase();
          const visibleOptions = query
            ? (val.options ?? []).filter((op: any) =>
                String(op.value).toLowerCase().includes(query)
              )
            : (val.options ?? []);

          return (
            <>
              <DropdownMenu
                onOpenChange={(open) => {
                  if (!open) setSearch("");
                }}
              >
                <DropdownMenuTrigger asChild className="w-full">
                  <button
                    type="button"
                    className={`w-full flex items-center justify-between rounded-2xl border bg-background px-3 py-2 text-sm transition-all focus:outline-none ${
                      scopeInvalid ? "border-red-500" : "border-input"
                    }`}
                  >
                    <span className="text-muted-foreground">
                      {selectedValues.length
                        ? `${selectedValues.length} selected`
                        : `Select ${getCfDisplayName(val).toLowerCase()}`}
                    </span>
                    <ChevronDown className="h-4 w-4 opacity-50" />
                  </button>
                </DropdownMenuTrigger>

                <DropdownMenuContent
                  align="start"
                  sideOffset={4}
                  className="
                    w-[var(--radix-dropdown-menu-trigger-width)]
                    min-w-[var(--radix-dropdown-menu-trigger-width)]
                    max-w-[var(--radix-dropdown-menu-trigger-width)]
                    bg-white rounded-xl shadow-xl border p-1 z-[50]
                  "
                >
                  {/* Radix runs its own typeahead on printable keys, so the
                      search box must swallow keystrokes before the menu sees
                      them — otherwise typing jumps focus between items. */}
                  <div
                    className="flex items-center gap-2 border-b px-2 pb-2 pt-1"
                    onKeyDown={(e) => e.stopPropagation()}
                  >
                    <Search className="h-3.5 w-3.5 shrink-0 opacity-50" />
                    <input
                      autoFocus
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder={`Search ${getCfDisplayName(val).toLowerCase()}…`}
                      className="w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
                    />
                  </div>

                  <div className="max-h-64 overflow-y-auto pt-1">
                    {visibleOptions.length === 0 ? (
                      <p className="px-2 py-3 text-sm text-muted-foreground">
                        No results found
                      </p>
                    ) : (
                      visibleOptions.map((op: any) => (
                        <DropdownMenuCheckboxItem
                          key={op.id}
                          checked={selectedValues.includes(String(op.id))}
                          onCheckedChange={() => toggleValue(String(op.id))}
                          onSelect={(e) => e.preventDefault()}
                          className="relative flex w-full cursor-pointer select-none items-center rounded-lg py-2.5 pl-10 pr-2 text-sm outline-none transition-colors focus:bg-slate-100"
                        >
                          <span className="truncate">{op.value}</span>
                        </DropdownMenuCheckboxItem>
                      ))
                    )}
                  </div>
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Selected Badges */}
              {selectedOptions.length > 0 && (
                <div className="flex flex-wrap gap-2 pt-1">
                  {selectedOptions.map((op: any) => (
                    <Badge key={op.id} variant="gray">
                      {op.value}
                    </Badge>
                  ))}
                </div>
              )}
            </>
          );
        }}
      />
      {scopeMsg ? (
        <p className="text-sm text-red-500">{scopeMsg}</p>
      ) : null}
    </div>
  );
}
